import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismRadioGroup } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import PowerCalendarRange from 'ember-power-calendar/components/power-calendar-range';
// eslint-disable-next-line import-x/no-named-as-default
import powerCalendarFormatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import { and, eq, not } from 'ember-truth-helpers';
import GeneralCalendarsDaysEmberPowerCalendarCustomDay from 'lumen/components/general/calendars/days/ember-power-calendar-custom-day';
import GeneralCalendarsMonthsCalendarMonthRangeTooltip from 'lumen/components/general/calendars/months/calendar-month-range-tooltip';
import dayjsToDate from 'lumen/helpers/dayjs-to-date';
import { isFixedPeriodVsReference } from 'lumen/utils/dates/moment-helpers';

// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function dayjsToDateRange(date) {
  if (!date) {
    return null;
  }

  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    end: dayjs(date.end).toDate(),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    start: dayjs(date.start).toDate(),
  };
}

interface GeneralCalendarsMonthsCalendarMonthRangeSignature {
  Args: {
    canSelectMonth: unknown;
    dateTooltips: unknown;
    eventData: unknown;
    highlightPeriod: unknown;
    hoveredDayMoment: unknown;
    hoveredRange: unknown;
    isDisabled: unknown;
    maxDate: unknown;
    maxRange: unknown;
    minDate: unknown;
    month: unknown;
    monthRange: unknown;
    selectRange: unknown;
  };
}

export default class GeneralCalendarsMonthsCalendarMonthRange extends Component<GeneralCalendarsMonthsCalendarMonthRangeSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  isMonthTitleHovered = false;
  // @ts-expect-error: Incorrect type
  todayFormatted = this.hotelAndMarketService
    .getLocalTimeWithFallback()
    .format('YYYY-MM-DD');

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMonthInRange() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.month
        .startOf('month')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .isSameOrAfter(this.args.minDate, 'day') &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.month.endOf('month').isSameOrBefore(this.args.maxDate, 'day')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMonthSelected() {
    // @ts-expect-error: Incorrect type
    if (!this.args.monthRange.start || !this.args.monthRange.end) {
      return false;
    }
    return isFixedPeriodVsReference(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.monthRange.start,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.monthRange.end,
      // @ts-expect-error: Incorrect type
      this.args.month,
      'month',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthFormat() {
    return this.dates.partialDateFormat?.YEAR_AND_MONTH_LONG;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get restrictedMaxDate() {
    // The PowerCalendar API does not have an option to disable the calendar entirely
    // We fake this functionality by setting the maxDate to 1 Jan 1970
    if (this.args.isDisabled) {
      return new Date(0);
    }
    return this.args.maxDate;
  }

  /**
   * selects a month
   * @param { string } month : string representation of a month
   */
  @action
  // @ts-expect-error: Incorrect type
  chooseMonth(month): void {
    if (this.isMonthInRange) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const monthMoment = dayjs(month);

      const newRange = {
        start: monthMoment.startOf('month'),
        end: monthMoment.endOf('month').endOf('day'),
      };

      // @ts-expect-error: Incorrect type
      this.args.selectRange({ moment: newRange });
    }
  }

  // Added specific action because power calendar adds arguments we don't need
  @action
  // @ts-expect-error: Incorrect type
  selectRangePowerCalendar(range): void {
    // Update moment.js objects to dayjs objects for internal use #EMBER-POWER-CALENDAR-DAYJS
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (range.moment.start) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      range.moment.start = dayjs(range.moment.start);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (range.moment.end) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      range.moment.end = dayjs(range.moment.end);
    }

    // @ts-expect-error: Incorrect type
    this.args.selectRange(range);
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div class="month-container">
      <div class="month-date-picker">
        <PowerCalendarRange
          {{! @glint-expect-error: Incorrect type }}
          @center={{@month}}
          {{! @glint-expect-error: Incorrect type }}
          @daysComponent={{GeneralCalendarsDaysEmberPowerCalendarCustomDay}}
          {{! @glint-expect-error: Incorrect type }}
          @maxRange={{@maxRange}}
          @onSelect={{this.selectRangePowerCalendar}}
          {{! @glint-expect-error: Incorrect type }}
          @selected={{@monthRange}}
          class="demo-calendar-small"
          as |calendar|
        >
          <calendar.Nav>
            {{#if @canSelectMonth}}
              <div
                class="month-title flex items-center justify-center"
                role="button"
                {{on "click" (fn this.chooseMonth calendar.center)}}
                {{on "mouseenter" (fn (mut this.isMonthTitleHovered) true)}}
                {{on "mouseleave" (fn (mut this.isMonthTitleHovered) false)}}
              >
                <PrismRadioGroup
                  @disabled={{not this.isMonthInRange}}
                  @onChange={{fn this.chooseMonth calendar.center}}
                  {{! @glint-expect-error: Incorrect type }}
                  @value={{this.isMonthSelected}}
                  as |RadioGroup|
                >
                  {{! @glint-expect-error: Incorrect type }}
                  <RadioGroup.input @value={{true}} class="mb-200">
                    {{powerCalendarFormatDate
                      calendar.center
                      this.monthFormat
                      locale=calendar.locale
                    }}
                  </RadioGroup.input>
                </PrismRadioGroup>
              </div>
            {{else}}
              {{powerCalendarFormatDate
                calendar.center
                this.monthFormat
                locale=calendar.locale
              }}
            {{/if}}
          </calendar.Nav>
          <calendar.Days
            {{! @glint-expect-error: Incorrect type }}
            @highlightPeriod={{@highlightPeriod}}
            {{! @glint-expect-error: Incorrect type }}
            @hoveredDayMoment={{dayjsToDate @hoveredDayMoment}}
            @hoveredRange={{dayjsToDateRange @hoveredRange}}
            {{! @glint-expect-error: Incorrect type }}
            @maxDate={{dayjsToDate this.restrictedMaxDate}}
            {{! @glint-expect-error: Incorrect type }}
            @minDate={{dayjsToDate @minDate}}
            @showDaysAround={{false}}
            @startOfWeek={{this.dates.startOfWeek}}
            @weekdayFormat="min"
            as |day|
          >
            <div
              class="day-cell relative
                {{if
                  (eq
                    (powerCalendarFormatDate day.date 'YYYY-MM-DD')
                    this.todayFormatted
                  )
                  'today'
                }}
                {{get
                  @eventData
                  (powerCalendarFormatDate day.date 'YYYY-MM-DD')
                }}
                {{if
                  (and this.isMonthInRange this.isMonthTitleHovered)
                  'month-hovered'
                }}"
            >
              {{day.number}}
              {{#if
                (get @eventData (powerCalendarFormatDate day.date "YYYY-MM-DD"))
              }}
                <div class="event-holiday-indication"></div>
              {{/if}}
            </div>

            <GeneralCalendarsMonthsCalendarMonthRangeTooltip
              @date={{powerCalendarFormatDate day.date "YYYY-MM-DD"}}
              @dateTooltips={{@dateTooltips}}
            />
          </calendar.Days>
        </PowerCalendarRange>
      </div>
    </div>
  </template>
}
